import React, { useState, useEffect } from 'react';

import { withRouter } from '@atlassian/react-resource-router';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fetchApplink, fetchApplinkStatus, getOAuth2ConfigInfo } from '../../rest/index.tsx';
import { LOG_LOCATION } from '../../common/constants.tsx';
import type { ApplicationLinkV3Status } from '../../common/entities.tsx';
import ApplinksEditPageContents, { type ApplinksProps } from './main.tsx';

export const ApplinksPage = (props: { id: string | null }) => {
	const [state, setState] = useState<{
		applinkStatus: ApplicationLinkV3Status | null;
		applinkEditPageData: ApplinksProps | null;
		isLoading: boolean;
	}>({
		applinkStatus: null,
		applinkEditPageData: null,
		isLoading: true,
	});

	useEffect(() => {
		const loadAllData = async () => {
			if (props.id === null) {
				log.safeErrorWithoutCustomerData(LOG_LOCATION, 'No applink ID provided');
				setState((prev) => ({ ...prev, applinkEditPageData: null, isLoading: false }));
				return;
			}

			setState((prev) => ({ ...prev, isLoading: true }));

			try {
				// Load applink data and status in parallel
				const [applinkData, status] = await Promise.all([
					fetchApplink(props.id),
					fetchApplinkStatus(props.id).catch((err) => {
						const error = err instanceof Error ? err : new Error('Unknown error');
						log.safeErrorWithoutCustomerData(LOG_LOCATION, 'Failed to fetch applink status', error);
						return null;
					}),
				]);

				let pageData: ApplinksProps | null = null;

				if (applinkData.system) {
					pageData = {
						kind: 'ERROR',
						err: 'NOT_EDITABLE',
					};
				} else if (applinkData.data.isOAuth2) {
					try {
						const configInfo = await getOAuth2ConfigInfo(applinkData.id);
						pageData = { kind: 'OAUTH2', applink: applinkData, oauth2ConfigInfo: configInfo };
					} catch (err) {
						const error = err instanceof Error ? err : new Error('Unknown error');
						log.safeErrorWithoutCustomerData(
							LOG_LOCATION,
							'Failed to fetch OAuth2 config info',
							error,
						);
						pageData = { kind: 'OAUTH2', applink: applinkData, oauth2ConfigInfo: null };
					}
				} else {
					pageData = { kind: 'OAUTH1', applink: applinkData };
				}

				setState({ applinkStatus: status, applinkEditPageData: pageData, isLoading: false });
			} catch (err) {
				const error = err instanceof Error ? err : new Error('Unknown error');
				log.safeErrorWithoutCustomerData(LOG_LOCATION, 'Failed to fetch applink data', error);
				setState((prev) => ({ ...prev, applinkEditPageData: null, isLoading: false }));
			}
		};

		loadAllData();
	}, [props.id]);

	return (
		<ApplinksEditPageContents
			isLoading={state.isLoading}
			data={state.applinkEditPageData}
			status={state.applinkStatus}
		/>
	);
};

export default withRouter(ApplinksPage);
